import React, {
    Component,
    MouseEvent
} from 'react';
import {
    bindActionCreators,
    Dispatch
} from 'redux';
import {connect} from 'react-redux';
import memoize from 'memoize-one';
import shallowEqual from 'shallowequal';
import _ from 'lodash';
import {keys} from 'ts-transformer-keys';
import parse from 'html-react-parser';

import {Button} from 'primereact/button';
import {
    Column,
    ColumnEvent,
    ColumnEditorOptions,
    ColumnBodyOptions
} from 'primereact/column';
import {ContextMenu} from 'primereact/contextmenu';
import {
    Dropdown,
    DropdownChangeEvent
} from 'primereact/dropdown';
import {
    DataTable,
    DataTableRowEvent,
    DataTableContextMenuSingleSelectionChangeEvent,
    DataTableSortMeta
} from 'primereact/datatable';
import {MenuItem} from 'primereact/menuitem';
import {Tooltip} from 'primereact/tooltip';

import {defaultBaseRequestParameters} from '~/shared/src/utilities/modules/common';
import {__} from '~/shared/src/utilities/messageCatalog';
import metaModel from '~/shared/src/utilities/metaModel';
import User from '~/shared/src/model/User';
import ReferencePropertySet from '~/shared/src/mm/ReferencePropertySet';
import {PropertyValueType} from '~/shared/src/mm/PropertySet';
import Reference, {ReferenceAttributesSettable} from '~/shared/src/ci/Reference';
import ReferenceProperty, {ReferencePropertyAttributesSettable} from '~/shared/src/ci/ReferenceProperty';
import {
    valuePrecision,
    getTargetArea
} from '~/shared/src/utilities/modules/finder';
import {convertValueToString} from '~/shared/src/utilities/valueConverters';
import {validateValueForType} from '~/shared/src/utilities/valueValidators';

import StatusDropdown from '~/frontend/src/components/base/shared/field/StatusDropdown';

import {StoreState} from '~/frontend/src/store';
import {
    getReferencePropertySetDisplayName,
    getValueDisplayName
} from '~/frontend/src/utilities/renderHelpers';
import {
    renderValue,
    statusColoringRenderer,
    statusRenderer
} from '~/frontend/src/utilities/valueRenderers';


import * as PanelActions from '~/frontend/src/actions/panel/common';
import {SetParamAction} from '~/frontend/src/actions/panel/common/setParam';
import {UnsetParamAction} from '~/frontend/src/actions/panel/common/unsetParam';
import * as DataActions from '~/frontend/src/actions/data';
import {UpdateReference} from '~/frontend/src/actions/data/updateReference';
import {
    getValueEditor,
    getValueSetEditor
} from '~/frontend/src/utilities/valueEditors';
import {timestampRenderer} from '~/shared/src/utilities/valueRenderers';

const Actions = Object.assign( {}, PanelActions, DataActions ) as any;
type SetParam = <K extends keyof AllProps>( panelKey : string, paramKey : K, paramValue : AllProps[K] ) => SetParamAction;
type UnsetParam = ( panelKey : string, paramKey : keyof AllProps ) => UnsetParamAction;


type DataTableState = { [ key : string ] : any };

const defaultProps = {
    ...defaultBaseRequestParameters,

    /**
     * Show empty (not set) properties.
     */
    showEmptyProperties: true as boolean,

    /**
     * Show default values for properties.
     */
    showPropertyDefaults: true as boolean,

    /**
     * Show the MetaModel information as defined (raw) <strong>or</strong> should they be processed by MessageCatalog.
     */
    rawMode: false as boolean,

    /**
     * Show the MetaModel information as defined additionally for processed information.
     * This applies in the form: &lt;processed MetaModel information&gt; (&lt;defined MetaModel information&gt;)
     *
     * Only applicable if <prop>rawMode</prop> is set to false.
     */
    showAdditionalRawNames: false as boolean,

    /**
     * Is data in this form editable at all?
     */
    editable: false as boolean,

    /**
     * The state for the DataTable
     */
    dataTableState: {
        multiSortMeta: [ { field: 'propertyTypeName', order: 1 } ]
    } as DataTableState
};

type DefaultProps = typeof defaultProps;

type Props = {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    /**
     * The dataContext to access global.data with.
     */
    dataContext : string;

    /**
     * The referenceId the properties belongs to.
     */
    referenceId : number;

    /**
     * The referenceSetId of the reference the properties belongs to.
     */
    referenceSetId : number;

    /**
     * The statusId of the reference the properties belongs to.
     */
    referenceStatusId : number;

    /**
     * The properties.
     *
     * Will be mapped from global state.
     */
    properties : Reference[ 'properties' ];

    /**
     * Parameters for deep linking of a property
     */
    deepLinkProps? : {
        property : string;
    };
};

type StateProps = {

    /**
     * The current user.
     *
     * Will be mapped from global state.
     */
    user? : User;
};

type ActionProps = {
    // common panel actions
    setParam : SetParam;
    unsetParam : UnsetParam;

    // data actions
    updateReference : UpdateReference;
}

export type ComponentProps = Partial<DefaultProps> & Props;
type AllProps = DefaultProps & Props & StateProps & ActionProps;

type State = {
    schema? : Schema;
    timezone? : string;
    referenceId? : number;
    referenceSetId? : number;
    properties? : Reference[ 'properties' ];
    rawMode? : boolean;
    showAdditionalRawNames? : boolean;
    showEmptyProperties? : boolean;
    showPropertyDefaults? : boolean;
    user? : User;
    //
    referencePropertySets : ReferencePropertySet[];
    insertableReferencePropertySets : ReferencePropertySet[];
    data : RowData[];
    multiSortMeta? : DataTableSortMeta[];
    contextMenuSelection? : any;
    contextMenuCellInfo? : {
        columnField : string;
        rowData : RowData;
    };
};
const stateKeys = keys<State>();

type RowData = {
    referencePropertySet? : ReferencePropertySet;
    propertyTypeName? : string;
    editPropertyTypeName? : string;
    valueType? : PropertyValueType | 'VALUESET';
    valueDefault? : string | JSX.Element | ( string | JSX.Element )[];
    referenceProperty? : ReferenceProperty;
    id? : number;
    value? : string | JSX.Element | ( string | JSX.Element )[];
    editValue? : AttributeValue;
    status? : string;
    editStatus? : Status;
    modifierUserId? : string;
    modificationTime? : NDate;
    validatorUserId? : string;
    validationTime? : NDate;
    validFromTime? : NDate;
    validToTime? : NDate;
    error? : string;
}

const activeStatusId = {
    CFG : metaModel?.[ 'CFG' ]?.getStatus( 'active' )?.id,
    DATA: metaModel?.[ 'DATA' ]?.getStatus( 'active' )?.id
};

const historyStatusId = {
    CFG : metaModel?.[ 'CFG' ]?.getStatus( 'history' )?.id,
    DATA: metaModel?.[ 'DATA' ]?.getStatus( 'history' )?.id
};

const deletedStatusId = {
    CFG : metaModel?.[ 'CFG' ]?.getStatus( 'deleted' )?.id,
    DATA: metaModel?.[ 'DATA' ]?.getStatus( 'deleted' )?.id
};

export const resetStatusIdCaches = () => {
    activeStatusId.CFG = metaModel[ 'CFG' ].getStatus( 'active' )?.id;
    activeStatusId.DATA = metaModel[ 'DATA' ].getStatus( 'active' )?.id;
    historyStatusId.CFG = metaModel[ 'CFG' ].getStatus( 'history' )?.id;
    historyStatusId.DATA = metaModel[ 'DATA' ].getStatus( 'history' )?.id;
    deletedStatusId.CFG = metaModel[ 'CFG' ].getStatus( 'deleted' )?.id;
    deletedStatusId.DATA = metaModel[ 'DATA' ].getStatus( 'deleted' )?.id;
};

/**
 * ReferencePropertiesDataTable
 */
class ReferencePropertiesDataTable extends Component<AllProps, State> {

    static defaultProps = defaultProps;

    /* properties *************************************************************************************************************************/

    contextMenu? : ContextMenu | null;
    dataTable? : DataTable<RowData[]> | null;
    lastDataTableState = {};
    ignoreDataTableSaveState = false as boolean;

    /**
     * REDUX stuff
     */
    constructor( props : AllProps ) {
        super( props );

        this.state = {
            referencePropertySets          : [],
            insertableReferencePropertySets: [],
            data                           : []
        };

        this.getColumns = memoize( this.getColumns );
        this.getContextMenuModel = memoize( this.getContextMenuModel );

        this.onAddRowButtonClick = this.onAddRowButtonClick.bind( this );
        this.onResolveRowClass = this.onResolveRowClass.bind( this );
        this.onBeforeCellEditShow = this.onBeforeCellEditShow.bind( this );
        this.onCellEditCancel = this.onCellEditCancel.bind( this );
        this.onCellEditComplete = this.onCellEditComplete.bind( this );
        this.onValueEditorResize = this.onValueEditorResize.bind( this );
        this.onCustomSaveState = this.onCustomSaveState.bind( this );
        this.onCustomRestoreState = this.onCustomRestoreState.bind( this );
        this.onContextMenuSelectionChange = this.onContextMenuSelectionChange.bind( this );
        this.onContextMenu = this.onContextMenu.bind( this );
        this.onContextMenuHide = this.onContextMenuHide.bind( this );

        this.columnActionRenderer = this.columnActionRenderer.bind( this );
        this.columnBodyRenderer = this.columnBodyRenderer.bind( this );

        this.getPropertyTypeEditor = this.getPropertyTypeEditor.bind( this );
        this.getValueEditor = this.getValueEditor.bind( this );
        this.getStatusEditor = this.getStatusEditor.bind( this );
    }

    /**
     * REACT lifecycle method
     */
    static getDerivedStateFromProps( props : AllProps, state : State ) {
        const retVal : Partial<State>                                                          = {},
              changedProps : ( Extract<keyof AllProps, keyof State> )[]                        = stateKeys.reduce(
                  ( innerRetVal : string[], prop ) => {
                      if (
                          props[ prop as keyof AllProps ] !== undefined
                          && props[ prop as keyof AllProps ] !== state[ prop ]
                      ) {
                          innerRetVal.push( prop as Extract<keyof AllProps, keyof State> );
                      }

                      return innerRetVal;
                  },
                  []
              ) as ( Extract<keyof AllProps, keyof State> )[],
              propertySetsChangingProps : ( Extract<keyof AllProps, keyof State> )[]           = [
                  'schema',
                  'referenceSetId',
                  'user'
              ],
              computePropertySets                                                              = !state.data.length || changedProps.some( prop => propertySetsChangingProps.includes( prop as Extract<keyof AllProps, keyof State> ) ),
              insertablePropertySetsChangingProps : ( Extract<keyof AllProps, keyof State> )[] = [
                  'schema',
                  'referenceSetId',
                  'properties',
                  'user'
              ],
              computeInsertablePropertySets                                                    = !state.data.length || changedProps.some( prop => insertablePropertySetsChangingProps.includes( prop as Extract<keyof AllProps, keyof State> ) ),
              dataChangingProps : ( Extract<keyof AllProps, keyof State> )[]                   = [
                  'schema',
                  'timezone',
                  'properties',
                  'rawMode',
                  'showAdditionalRawNames',
                  'showEmptyProperties',
                  'showPropertyDefaults',
                  'user'
              ],
              computeData                                                                      = !state.data.length || changedProps.some( prop => dataChangingProps.includes( prop as Extract<keyof AllProps, keyof State> ) );

        // compute PropertySets?
        if ( computePropertySets ) {
            retVal.referencePropertySets = ReferencePropertiesDataTable.getReferencePropertySets( props );
        }

        // compute insertable PropertyTypes?
        if ( computeInsertablePropertySets ) {
            retVal.insertableReferencePropertySets = ReferencePropertiesDataTable.getInsertableReferencePropertySets( props );
        }

        // compute data?
        if ( computeData ) {
            const referencePropertySets = retVal.referencePropertySets ?? state.referencePropertySets;

            retVal.data = ReferencePropertiesDataTable.getData( props, referencePropertySets );
        }

        // sort data?
        if (
            props.dataTableState.multiSortMeta?.length
            && (
                computeData
                || !shallowEqual( props.dataTableState.multiSortMeta, state.multiSortMeta )
            )
        ) {
            const data = retVal.data ?? ( [] as RowData[] ).concat( state.data );

            retVal.data = ReferencePropertiesDataTable.sortData( data, props.dataTableState.multiSortMeta );
        }

        // update state
        for ( const prop of changedProps ) {
            retVal[ prop as keyof State ] = props[ prop ];
        }
        if ( props.dataTableState.multiSortMeta !== state.multiSortMeta ) {
            retVal.multiSortMeta = props.dataTableState.multiSortMeta;
        }

        return retVal;
    }

    /**
     * Gets the PropertySets.
     *
     * @param props
     */
    static getReferencePropertySets( props : AllProps ) : ReferencePropertySet[] {
        const retVal : ReferencePropertySet[] = [],
              {
                  schema,
                  referenceSetId,
                  user
              }                               = props,
              referencePropertySets           = metaModel[ schema ].getReferencePropertySetsForReferenceSet( referenceSetId );

        for ( const referencePropertySet of referencePropertySets ) {
            if (
                !referencePropertySet.isEnumerated
                && user?.hasPermissionOnReferencePropertySet( 'read', referencePropertySet.id, schema )
                && user?.hasGenericPermissionOnReferencePropertySet( 'read', referencePropertySet.id, schema )
            ) {
                retVal.push( referencePropertySet );
            }
        }

        return retVal;
    }

    /**
     * Gets the PropertySets.
     *
     * @param props
     */
    static getInsertableReferencePropertySets( props : AllProps ) : ReferencePropertySet[] {
        const retVal : ReferencePropertySet[] = [],
              {
                  schema,
                  referenceSetId,
                  properties,
                  user
              }                               = props,
              referencePropertySets           = metaModel[ schema ].getReferencePropertySetsForReferenceSet( referenceSetId );

        for ( const referencePropertySet of referencePropertySets ) {
            if (
                !referencePropertySet.isEnumerated
                && !properties.has( referencePropertySet.propertyTypeId )
                && user?.hasPermissionOnReferencePropertySet( 'insert', referencePropertySet.id, schema )
                && user?.hasGenericPermissionOnReferencePropertySet( 'nonBlocked', referencePropertySet.id, schema )
            ) {
                retVal.push( referencePropertySet );
            }
        }

        return retVal;
    }

    /**
     * Gets the data for DataTable.
     *
     * @param props
     * @param referencePropertySets
     */
    static getData( props : AllProps, referencePropertySets : ReferencePropertySet[] ) : RowData[] {
        const retVal : RowData[] = [],
              {
                  schema,
                  timezone,
                  properties,
                  rawMode,
                  showAdditionalRawNames,
                  showEmptyProperties,
                  showPropertyDefaults,
                  user
              }                  = props;

        for ( const referencePropertySet of referencePropertySets ) {
            const referenceProperty = properties.get( referencePropertySet.propertyTypeId );

            let value : string | JSX.Element | ( string | JSX.Element )[] | undefined,
                valueDefault : string | JSX.Element | ( string | JSX.Element )[] | undefined;

            if ( referenceProperty || showEmptyProperties ) {

                if ( referencePropertySet.valueSetId ) {
                    if ( referenceProperty ) {
                        value = parse( getValueDisplayName( referencePropertySet.valueSetId, convertValueToString( referenceProperty.value, referencePropertySet.valueType ), schema, rawMode, showAdditionalRawNames ) );
                    }
                    if ( referencePropertySet.valueDefault !== undefined ) {
                        valueDefault = parse( getValueDisplayName( referencePropertySet.valueSetId, convertValueToString( referencePropertySet.valueDefault, referencePropertySet.valueType ), schema, rawMode, showAdditionalRawNames ) );
                    }
                }
                else {
                    const params = {
                        //filterString: '',
                        language : user?.language,
                        schema,
                        showAdditionalRawNames,
                        timezone,
                        valueUnit: referencePropertySet.valueUnit,
                        valuePrecision,
                        editMode : true
                    };

                    if ( referenceProperty ) {
                        value = renderValue( referencePropertySet.valueType, referenceProperty.value, params, rawMode );
                    }
                    if ( referencePropertySet.valueDefault !== undefined && referencePropertySet.valueDefault !== null && referencePropertySet.valueDefault !== '' ) {
                        valueDefault = renderValue( referencePropertySet.valueType, referencePropertySet.valueDefault, params, rawMode );
                    }
                }

                retVal.push( {
                    referencePropertySet: referencePropertySet,
                    propertyTypeName    : getReferencePropertySetDisplayName( referencePropertySet.id, schema, rawMode, showAdditionalRawNames ),
                    valueType           : referencePropertySet.valueSetId ? 'VALUESET' : referencePropertySet.valueType,
                    valueDefault        : showPropertyDefaults ? valueDefault : undefined,
                    referenceProperty   : referenceProperty,
                    value               : value,
                    status              : referenceProperty ? statusRenderer( referenceProperty.statusId, schema, rawMode, showAdditionalRawNames ) : undefined,
                    modifierUserId      : referenceProperty?.modifierUserId,
                    modificationTime    : referenceProperty?.modificationTime,
                    validatorUserId     : referenceProperty?.validatorUserId,
                    validationTime      : referenceProperty?.validationTime,
                    validFromTime       : referenceProperty?.validFromTime,
                    validToTime         : referenceProperty?.validToTime
                } );
            }
        }

        return retVal;
    }

    /**
     * Sorts the data for the DataTable.
     *
     * @param data
     * @param multiSortMeta
     *
     * @private
     */
    static sortData( data : RowData[], multiSortMeta : DataTableSortMeta[] ) : RowData[] {
        const sortFnc = function( rowData1 : RowData, rowData2 : RowData, sortMeta : DataTableSortMeta[], index : number ) : number {
            const attribute = sortMeta[ index ].field as keyof RowData,
                  value1    = rowData1[ attribute ]!,
                  value2    = rowData2[ attribute ]!;

            let result = null;

            if ( rowData1.id === 0 ) {
                result = -sortMeta[ index ].order!;
            }
            else if ( rowData2.id === 0 ) {
                result = sortMeta[ index ].order!;
            }
            else if ( value1 !== value2 ) {
                result = value1 < value2 ? -1 : 1;
            }
            else {
                return sortMeta.length - 1 > index ? sortFnc( rowData1, rowData2, sortMeta, index + 1 ) : 0;
            }

            return sortMeta[ index ].order! * result;
        };

        if ( multiSortMeta?.length ) {
            data = data.sort( ( rowData1, rowData2 ) => {
                return sortFnc( rowData1, rowData2, multiSortMeta, 0 );
            } );
        }

        return data;
    }

    /**
     * REACT lifecycle method
     */
    componentDidMount() {
        this.setDataTableState();
        this.processDeepLink();
    }

    /**
     * REACT lifecycle method
     */
    componentDidUpdate() {
        //this.setDataTableState();
        this.processDeepLink();
    }


    /* event handlers *********************************************************************************************************************/

    /**
     * Handler for DataTable rowClass callback.
     *
     * @private
     */
    onResolveRowClass( rowData : RowData ) : { [ className : string ] : boolean } {
        const retVal : { [ className : string ] : boolean } = {},
              schema                                        = this.props.schema,
              referenceProperty                             = rowData.referenceProperty;

        if ( historyStatusId[ schema ] === undefined ) {
            resetStatusIdCaches();
        }

        if ( referenceProperty ) {
            // mark added row
            if ( referenceProperty.id < 0 ) {
                retVal[ 'yn-added' ] = true;
            }

            // mark deleted row
            else if (
                referenceProperty?.isAttributeModified( 'statusId' )
                && (
                    referenceProperty.statusId === historyStatusId[ schema ]
                    || referenceProperty.statusId === deletedStatusId[ schema ]
                )
            ) {
                retVal[ 'yn-deleted' ] = true;
            }

            // mark modified row
            else if ( referenceProperty?.isModified() ) {
                retVal[ 'yn-modified' ] = true;
            }
        }

        return retVal;
    }

    /**
     * Handler for all editors beforeShow
     *
     * @private
     */
    onBeforeCellEditShow( params : ColumnEvent ) {
        const {
                  schema,
                  editable,
                  showEmptyProperties
              }       = this.props,
              field   = params.field,
              rowData = this.state.data[ params.rowIndex ],
              {
                  referencePropertySet,
                  referenceProperty
              }       = rowData;

        let retVal : boolean = false,
            value            = rowData.editValue ?? referenceProperty?.value ?? referencePropertySet?.valueDefault;

        // editable?
        if (
            editable
            && (
                (
                    referencePropertySet
                    && field !== 'propertyTypeName'
                    && (
                        (
                            referenceProperty
                            && this.isReferencePropertySetUpdatable( referencePropertySet.id )
                            && referenceProperty.statusId !== historyStatusId[ schema ]
                            && referenceProperty.statusId !== deletedStatusId[ schema ]
                        )
                        || (
                            !referenceProperty
                            && this.isReferencePropertySetInsertable( referencePropertySet.id )
                        )
                    )
                )
                || (
                    !referencePropertySet
                    && field === 'propertyTypeName'
                    && !showEmptyProperties
                )
            )
        ) {
            retVal = true;
        }

        if ( retVal ) {
            // setting boolean value directly and deny editing
            if ( field === 'value' && referencePropertySet?.valueType === 'BOOLEAN' ) {
                value = referenceProperty
                    ? !referenceProperty.value
                    : referencePropertySet.valueDefault === '' ? true : !referencePropertySet.valueDefault;

                this.setReferencePropertyAttributes( referencePropertySet.propertyTypeId, { value } );

                retVal = false;
            }

            // deny editing for new property (unsaved) - allow only propertyTypeName
            else if ( !referencePropertySet && field !== 'propertyTypeName' ) {
                retVal = false;
            }

            // deny editing status for new property (unsaved)
            else if ( !referenceProperty && field === 'status' ) {
                retVal = false;
            }
        }

        return retVal;
    }

    /**
     * Handler for all editors cancel
     * Restore the initial value of editor init or delete just added property
     *
     * @private
     */
    onCellEditCancel( params : ColumnEvent ) {
        this.setState( { data: [] } );
    }

    /**
     * Handler for editors submit
     *
     * @private
     */
    onCellEditComplete( params : ColumnEvent ) {
        const {
                  schema,
                  timezone,
                  referenceStatusId,
                  referenceSetId,
                  rawMode,
                  showAdditionalRawNames,
                  user
              }                  = this.props,
              data               = ( [] as RowData[] ).concat( this.state.data ),
              field              = params.field,
              rowData            = data[ params.rowIndex ],
              {
                  referencePropertySet,
                  referenceProperty
              }                  = rowData,
              editValueAttribute = `edit${_.upperFirst( field )}` as keyof RowData,
              value              = rowData[ editValueAttribute ] as AttributeValue,
              error              = rowData.error,
              attributes         = {} as ReferencePropertyAttributesSettable;

        // handle changes
        if ( value !== undefined && value !== null && value !== '' && !error ) {

            // existing property
            if ( referencePropertySet && referenceProperty ) {
                if ( field === 'value' ) {
                    attributes[ 'value' ] = value;
                }
                else if ( field === 'status' ) {
                    attributes[ 'statusId' ] = metaModel[ this.props.schema ].getStatus( value as string )?.id
                }

                this.setReferencePropertyAttributes( referencePropertySet.propertyTypeId, attributes );
            }

            // new property (unsaved)
            else {
                if ( field === 'propertyTypeName' ) {
                    const newReferencePropertySet = metaModel[ schema ].getReferencePropertySetForReferenceSetAndPropertyType( referenceSetId, value as string );

                    if ( newReferencePropertySet && newReferencePropertySet !== rowData.referencePropertySet ) {
                        rowData.referencePropertySet = newReferencePropertySet;
                        rowData.propertyTypeName = getReferencePropertySetDisplayName( newReferencePropertySet.id, schema, rawMode, showAdditionalRawNames );
                    }
                }
                else if ( field === 'value' && referencePropertySet ) {
                    rowData.editValue = value;

                    if ( referencePropertySet.valueSetId ) {
                        rowData.value = parse( getValueDisplayName( referencePropertySet.valueSetId, convertValueToString( value, referencePropertySet.valueType ), schema, rawMode, showAdditionalRawNames ) );
                    }
                    else {
                        rowData.value = renderValue(
                            referencePropertySet.valueType,
                            value,
                            {
                                //filterString: '',
                                language : user?.language,
                                showAdditionalRawNames,
                                timezone,
                                valueUnit: referencePropertySet.valueUnit,
                                valuePrecision,
                                editMode : true
                            },
                            rawMode
                        );
                    }
                }

                // propertySet and value set -> create property
                if (
                    rowData.referencePropertySet
                    && rowData.editValue !== undefined && rowData.editValue !== null && rowData.editValue !== ''
                ) {
                    this.setReferencePropertyAttributes( rowData.referencePropertySet.propertyTypeId, {
                        value   : rowData.editValue,
                        statusId: referenceStatusId
                    } );

                    this.dataTable?.reset();
                    this.setState( { data: [] } );
                }

                // save edited value in rowData
                else {
                    this.setState( { data } );
                }
            }
        }

        // clear editValueAttribute and error in rowData
        else {
            this.setState( { data: [] } );
        }
    }

    /**
     * Handler for value editor resize
     *
     * @private
     */
    onValueEditorResize( width : number, height : number ) {
        const dataTableState = { ...this.props.dataTableState },
              columnWidths   = dataTableState?.columnWidths?.split( ',' );

        if ( columnWidths?.length ) {
            let valueColumnIndex = 2;

            if ( dataTableState?.columnOrder?.length ) {
                for ( let i = 0; i < dataTableState.columnOrder.length; i++ ) {
                    if ( dataTableState.columnOrder[ i ] === 'value' ) {
                        valueColumnIndex = i;
                        break;
                    }
                }
            }
            columnWidths[ valueColumnIndex ] = width;
            dataTableState.columnWidths = columnWidths.join( ',' )

            this.props.setParam( `${this.props.uid}`, 'dataTableState', dataTableState );
        }
    }

    /**
     * Handler for propertyType editor change
     *
     * @private
     */
    onPropertyTypeEditorChange( options : ColumnEditorOptions, event : DropdownChangeEvent ) {
        const {
                  schema,
                  timezone,
                  referenceSetId,
                  rawMode,
                  showAdditionalRawNames,
                  user
              }                       = this.props,
              value                   = event.value,
              rowData                 = this.state.data[ options.rowIndex ],
              newReferencePropertySet = metaModel[ schema ].getReferencePropertySetForReferenceSetAndPropertyType( referenceSetId, value as string );

        rowData.editPropertyTypeName = value;

        if ( newReferencePropertySet ) {
            rowData.valueType = newReferencePropertySet.valueSetId ? 'VALUESET' : newReferencePropertySet.valueType;
            if ( newReferencePropertySet.valueSetId ) {
                rowData.value = parse( getValueDisplayName( newReferencePropertySet.valueSetId, convertValueToString( newReferencePropertySet.valueDefault, newReferencePropertySet.valueType ), schema, rawMode, showAdditionalRawNames ) );
            }
            else {
                rowData.value = renderValue(
                    newReferencePropertySet.valueType,
                    newReferencePropertySet.valueDefault,
                    {
                        //filterString: '',
                        language : user?.language,
                        showAdditionalRawNames,
                        timezone,
                        valueUnit: newReferencePropertySet.valueUnit,
                        valuePrecision,
                        editMode : true
                    },
                    rawMode
                );
            }
            rowData.editValue = newReferencePropertySet.valueDefault;
        }

        options?.editorCallback?.( value );
    }

    /**
     * Handler for value editor change
     *
     * @private
     */
    onValueEditorChange( options : ColumnEditorOptions, value : AttributeValue | undefined, originalEvent : any ) {
        const rowData = this.state.data[ options.rowIndex ];

        rowData.editValue = value as AttributeValue;
        delete rowData.error;

        if ( value === undefined || value === null || value === '' ) {
            rowData.error = __( 'YN.base.finder.property.value.error.empty' );
        }
        else if ( rowData.referencePropertySet ) {
            try {
                validateValueForType( value, rowData.referencePropertySet.valueType, true );
            }
            catch ( e ) {
                if ( e instanceof Error ) {
                    rowData.error = e.toString();
                }
                else {
                    rowData.error = String( e );
                }
            }
        }

        options?.editorCallback?.( value );
    }

    /**
     * Handler for status editor change
     *
     * @private
     */
    onStatusEditorChange( options : ColumnEditorOptions, status : Status ) {
        const rowData = this.state.data[ options.rowIndex ];

        rowData.editStatus = status;

        options?.editorCallback?.( status );
    }

    /**
     * Handler for add row button click.
     *
     * @private
     */
    onAddRowButtonClick() {
        const data = ( [] as RowData[] ).concat( this.state.data );

        data.unshift( { id: 0 } );

        this.setState( { data } );
    }

    /**
     * Handler for action column button click
     *
     * @private
     */
    onActionButtonClick( type : 'delete' | 'restore' | 'physicalDelete' | 'physicalRestore', rowData : RowData, event : MouseEvent<HTMLButtonElement> ) {
        const {
                  schema,
                  referenceStatusId
              }                 = this.props,
              referenceProperty = rowData.referenceProperty;

        let statusId : number;

        if ( historyStatusId[ schema ] === undefined ) {
            resetStatusIdCaches();
        }

        // existing referenceProperty
        if ( referenceProperty ) {
            switch ( type ) {
                case 'delete':
                    statusId = historyStatusId[ schema ]!;
                    break;

                case'physicalDelete':
                    statusId = deletedStatusId[ schema ]!;
                    break;

                case 'restore':
                case 'physicalRestore':
                    statusId = referenceProperty.attributesModified.get( 'statusId' ) as number ?? referenceStatusId;
                    break;
            }

            this.setReferencePropertyAttributes( referenceProperty.referencePropertySet.propertyTypeId, { statusId } );
        }

        // new referenceProperty (unsaved)
        else if ( type === 'delete' || type === 'physicalDelete' ) {
            const data = ( [] as RowData[] ).concat( this.state.data );

            data.splice( data.indexOf( rowData ), 1 );

            this.setState( { data } );
        }

        event.stopPropagation();
        event.preventDefault();
    }

    /**
     * Handler for DataTable onCustomSaveState event to extract the columns, filters and sorters
     *
     * @param {DataTableState} dataTableState
     *
     * @private
     */
    onCustomSaveState( dataTableState : DataTableState ) {
        if ( !this.ignoreDataTableSaveState && !this.ignoreDataTableSaveState && !shallowEqual( dataTableState, this.props.dataTableState ) ) {
            this.props.setParam( `${this.props.uid}`, 'dataTableState', dataTableState );
        }

        this.ignoreDataTableSaveState = false;
    }

    /**
     * Handler for DataTable onCustomRestoreState event to inject the columns, filters and sorters
     *
     * @return {DataTableState}
     *
     * @private
     */
    onCustomRestoreState() {
        const dataTableState = {
            ...this.props.dataTableState
        };

        dataTableState.tableWidth = undefined;

        return dataTableState;
    }

    /**
     * Handler for dataTable contextMenuSelectionChange
     *
     * @private
     */
    onContextMenuSelectionChange( event : DataTableContextMenuSingleSelectionChangeEvent<RowData[]> ) {
        const rowData = event.value,
              field   = this.getFieldForMouseEvent( event.originalEvent as unknown as MouseEvent ) ?? 'n/a';

        this.setState( {
            contextMenuSelection: event.value,
            contextMenuCellInfo : {
                columnField: field,
                rowData
            }
        } );
    }

    /**
     * Handler for dataTable contextMenu
     *
     * @private
     */
    onContextMenu( event : DataTableRowEvent ) {
        this.contextMenu?.show( event.originalEvent );
    }

    /**
     * Handler for contextMenu hide
     *
     * @private
     */
    onContextMenuHide() {
        this.setState( {
            contextMenuSelection: undefined,
            contextMenuCellInfo : undefined
        } );
    }


    /* private ****************************************************************************************************************************/

    /**
     * Processes given deepLinkProps.
     *
     * @private
     */
    processDeepLink() {
        if ( this.props.deepLinkProps ) {
            const {
                      uid,
                      referenceSetId,
                      deepLinkProps
                  }                      = this.props,
                  referencePropertySetId = metaModel[ schema ].getReferencePropertySetForReferenceSetAndPropertyType( referenceSetId, deepLinkProps.property )?.id;

            const clearDeepLinkProps = true;

            if ( referencePropertySetId ) {
                const {
                          data
                      }        = this.state,
                      rowIndex = data.findIndex( rowData => rowData.referencePropertySet?.id === referencePropertySetId );

                if ( rowIndex ) {
                    const dataTableRows = document.querySelectorAll( `#${uid}_properties .p-datatable-tbody tr` ) as NodeListOf<HTMLElement>,
                          row           = dataTableRows?.[ rowIndex ];

                    if ( row ) {
                        row.scrollIntoView( {
                            behavior: 'smooth',
                            block   : 'center',
                            inline  : 'nearest'
                        } );
                    }
                }
            }

            if ( clearDeepLinkProps ) {
                this.props.unsetParam( uid, 'deepLinkProps' );
            }
        }
    }

    /**
     * Sets the DataTable's state
     *
     * @private
     */
    setDataTableState() {
        const dataTableState = this.onCustomRestoreState();

        if ( !_.isEqual( this.lastDataTableState, dataTableState ) ) {
            this.lastDataTableState = dataTableState;

            this.dataTable?.reset();
            this.dataTable?.restoreTableState( dataTableState );
        }
    }

    /**
     * Builds the contextMenu content for current selected row.
     */
    getContextMenuModel( contextMenuCellInfo : State['contextMenuCellInfo'] ) : MenuItem[] {
        const retVal : MenuItem[] = [],
              {
                  editable
              }                   = this.props;

        if ( editable && contextMenuCellInfo ) {
            const referenceProperty = contextMenuCellInfo.rowData.referenceProperty;

            // revert
            if ( referenceProperty?.isModified() && this.isReferencePropertySetUpdatable( referenceProperty.referencePropertySetId ) ) {
                retVal.push(
                    {
                        label  : __( 'YN.base.frontend.dataTable.contextMenu.revert' ),
                        icon   : 'pi pi-fw pi-undo',
                        command: () => {
                            const attributes : any = {};

                            referenceProperty.attributesModified.forEach( ( value, key ) => {
                                attributes[ key ] = value;
                            } );

                            this.setReferencePropertyAttributes( referenceProperty.referencePropertySet.propertyTypeId, attributes );
                        }
                    }
                );
            }
        }

        return retVal;
    }

    /**
     * Gets the columnField for an mouse event if it is possible
     * @param event
     */
    getFieldForMouseEvent( event : MouseEvent ) : string | undefined {
        let retVal : string | undefined;

        const {
                  status,
                  timestamp,
                  showEmptyProperties,
                  editable,
                  user
              }             = this.props,
              {
                  insertableReferencePropertySets
              }             = this.state,
              targetArea    = getTargetArea( status, timestamp ),
              columns       = this.getColumns( targetArea, showEmptyProperties, editable, insertableReferencePropertySets, user?.language ),
              targetElement = event.target as unknown as Element;

        // get TD element
        let cellElement : Element | null       = targetElement.parentElement,
            cellNode : Node | null | undefined = targetElement.parentNode;
        while ( cellElement !== null && cellElement.tagName !== 'TD' ) {
            cellElement = cellElement.parentElement;
            cellNode = cellNode?.parentNode;
        }

        // get row
        const rowElement = cellElement?.parentElement;

        // get columnIndex
        let columnIndex : number | undefined;
        if ( rowElement ) {
            for ( let i = 0; i < rowElement.childNodes.length; i++ ) {
                if ( rowElement.childNodes[ i ] === cellNode ) {
                    columnIndex = i;
                    break;
                }
            }
        }

        // get field
        if ( columnIndex !== undefined ) {
            retVal = columns[ columnIndex ]?.props?.field;
        }

        return retVal;
    }

    /**
     * Empty handler method.
     *
     * @private
     */
    emptyFnc( event : MouseEvent<HTMLButtonElement> ) {
        event.stopPropagation();
        event.preventDefault();
    }

    isReferencePropertySetReadable( referencePropertySetId : number ) : boolean {
        const {
                  schema,
                  user
              } = this.props;

        let retVal = false;

        if (
            user?.hasPermissionOnReferencePropertySet( 'read', referencePropertySetId, schema )
            && user?.hasGenericPermissionOnReferencePropertySet( 'read', referencePropertySetId, schema )
        ) {
            retVal = true;
        }

        return retVal;
    }

    isReferencePropertySetUpdatable( referencePropertySetId : number ) : boolean {
        const {
                  schema,
                  user
              } = this.props;

        let retVal = false;

        if (
            user?.hasPermissionOnReferencePropertySet( 'update', referencePropertySetId, schema )
            && user?.hasGenericPermissionOnReferencePropertySet( 'nonBlocked', referencePropertySetId, schema )
        ) {
            retVal = true;
        }

        return retVal;
    }

    isReferencePropertySetInsertable( referencePropertySetId : number ) : boolean {
        const {
                  schema,
                  user
              } = this.props;

        let retVal = false;

        if (
            user?.hasPermissionOnReferencePropertySet( 'insert', referencePropertySetId, schema )
            && user?.hasGenericPermissionOnReferencePropertySet( 'nonBlocked', referencePropertySetId, schema )
        ) {
            retVal = true;
        }

        return retVal;
    }

    isReferencePropertySetDeletable( referencePropertySetId : number ) : boolean {
        const {
                  schema,
                  user
              } = this.props;

        let retVal = false;

        if (
            user?.hasPermissionOnReferencePropertySet( 'delete', referencePropertySetId, schema )
            && user?.hasGenericPermissionOnReferencePropertySet( 'nonBlocked', referencePropertySetId, schema )
        ) {
            retVal = true;
        }

        return retVal;
    }

    /**
     * Sets the given attribute to given value for this items property given by propertyTypeId
     *
     * @param propertyTypeId
     * @param attributesToSet
     */
    setReferencePropertyAttributes( propertyTypeId : number, attributesToSet : ReferencePropertyAttributesSettable ) {
        const {
                  dataContext,
                  referenceId
              }                                  = this.props,
              propertyTypeIdString               = String( propertyTypeId ),
              data : ReferenceAttributesSettable = {};

        data.properties = {};
        data.properties[ propertyTypeIdString ] = attributesToSet;

        this.props.updateReference( dataContext, referenceId, data );
    }

    /**
     * Builds and returns the columns for properties DataTable.
     *
     * @param targetArea
     * @param showEmptyProperties
     * @param editable
     * @param insertableReferencePropertySets
     * @param language
     *
     * @private
     */
    getColumns( targetArea : 'active' | 'all', showEmptyProperties : boolean, editable : boolean, insertableReferencePropertySets : ReferencePropertySet[], language? : string ) : JSX.Element[] {
        const retVal : JSX.Element[] = [],
              columnDefaults         = {
                  sortable       : true,
                  body           : this.columnBodyRenderer,
                  bodyClassName  : 'yn-nowrap-top-column yn-nopadding-column',
                  headerClassName: 'yn-header-column'
              };

        if ( editable ) {
            let header : string | JSX.Element = '';

            if ( !showEmptyProperties && insertableReferencePropertySets.length ) {
                header = <Button
                    key="addRowButton"

                    onClick={this.onAddRowButtonClick}

                    icon="pi pi-plus-circle"
                    className="yn-action-button yn-green-button"
                />;
            }
            else {
                header = <Button
                    key="addRowButton"

                    onClick={this.emptyFnc}

                    icon="pi pi-plus-circle"
                    className="yn-action-button yn-green-button"

                    disabled={true}
                    style={{ opacity: 0 }}
                />;
            }

            retVal.push( <Column
                key="deleteOrRestore"
                field="deleteOrRestore"
                header={header}

                filter={false}
                reorderable={false}
                sortable={false}
                resizeable={false}

                body={this.columnActionRenderer}
                bodyClassName={`${columnDefaults.bodyClassName} yn-action-column`}
                headerClassName="yn-header-action-column"
                filterHeaderClassName="yn-header-action-column"
            /> );
        }

        retVal.push( <Column
            key="propertyTypeName"
            field="propertyTypeName"
            header={__( 'YN.base.finder.detail.properties.propertyTypeName' )}
            {...columnDefaults}
            style={{ width: '15rem', minWidth: '10rem' }}

            editor={this.getPropertyTypeEditor}
            onBeforeCellEditShow={this.onBeforeCellEditShow}
            onCellEditCancel={this.onCellEditCancel}
            onCellEditComplete={this.onCellEditComplete}
        /> );

        retVal.push( <Column
            key="value"
            field="value"
            header={__( 'YN.base.finder.detail.properties.value' )}
            {...columnDefaults}
            bodyClassName={`${columnDefaults.bodyClassName} yn-column`}
            style={{ width: 'auto', minWidth: '15rem' }}

            editor={this.getValueEditor}
            onBeforeCellEditShow={this.onBeforeCellEditShow}
            onCellEditCancel={this.onCellEditCancel}
            onCellEditComplete={this.onCellEditComplete}
        /> );

        retVal.push( <Column
            key="valueType"
            field="valueType"
            header={__( 'YN.base.finder.detail.properties.valueType' )}
            {...columnDefaults}
            style={{ width: '8rem' }}
        /> );

        retVal.push( <Column
            key="status"
            field="status"
            header={__( 'YN.base.finder.status' )}
            {...columnDefaults}
            style={{ width: '5rem' }}

            editor={this.getStatusEditor}
            onBeforeCellEditShow={this.onBeforeCellEditShow}
            onCellEditCancel={this.onCellEditCancel}
            onCellEditComplete={this.onCellEditComplete}
        /> );

        // metaData

        // active
        if ( targetArea === 'active' ) {
            retVal.push( <Column
                key="modificationTime"
                field="modificationTime"
                header={__( 'YN.base.finder.modifiedAt' )}
                {...columnDefaults}
                style={{ width: '10.2rem' }}
            /> );
            retVal.push( <Column
                key="modifierUserId"
                field="modifierUserId"
                header={__( 'YN.base.finder.modifiedBy' )}
                {...columnDefaults}
                style={{ width: '8rem' }}
            /> );

            retVal.push( <Column
                key="validationTime"
                field="validationTime"
                header={__( 'YN.base.finder.validatedAt' )}
                {...columnDefaults}
                style={{ width: '10.2rem' }}
            /> );
            retVal.push( <Column
                key="validatorUserId"
                field="validatorUserId"
                header={__( 'YN.base.finder.validatedBy' )}
                {...columnDefaults}
                style={{ width: '8rem' }}
            /> );
        }

        // all
        else {
            retVal.push( <Column
                key="modifierUserId"
                field="modifierUserId"
                header={__( 'YN.base.finder.modifiedBy' )}
                {...columnDefaults}
                style={{ width: '8rem' }}
            /> );

            retVal.push( <Column
                key="validFromTime"
                field="validFromTime"
                header={__( 'YN.base.finder.validFrom' )}
                {...columnDefaults}
                style={{ width: '10.2rem' }}
            /> );

            retVal.push( <Column
                key="validToTime"
                field="validToTime"
                header={__( 'YN.base.finder.validTo' )}
                {...columnDefaults}
                style={{ width: '10.2rem' }}
            /> );
        }

        return retVal;
    }

    /**
     * Column renderer
     *
     * @private
     */
    columnActionRenderer( rowData : RowData, columnProps : ColumnBodyOptions ) : string | JSX.Element | ( string | JSX.Element )[] | undefined {
        const {
                  schema
              } = this.props,
              {
                  id,
                  referenceProperty
              } = rowData;

        let retVal : string | JSX.Element | ( string | JSX.Element )[] = '',
            icon,
            className,
            onClickHandler,
            enabled                                                    = false;

        const isHistory = referenceProperty?.statusId === historyStatusId[ schema ],
              isDeleted = referenceProperty?.statusId === deletedStatusId[ schema ];

        if ( historyStatusId[ schema ] === undefined ) {
            resetStatusIdCaches();
        }

        // restore
        if ( referenceProperty && ( isHistory || isDeleted ) ) {
            icon = 'pi pi-plus-circle';
            className = 'yn-action-button yn-green-button';

            onClickHandler = this.onActionButtonClick.bind( this, 'physicalRestore', rowData );

            if (
                isHistory && this.isReferencePropertySetUpdatable( referenceProperty.referencePropertySetId )
                || isDeleted && this.isReferencePropertySetInsertable( referenceProperty.referencePropertySetId )
            ) {
                enabled = true;
            }
        }

        // delete
        else if ( referenceProperty || id === 0 ) {
            icon = 'pi pi-minus-circle';
            className = 'yn-action-button yn-red-button';

            onClickHandler = this.onActionButtonClick.bind( this, 'physicalDelete', rowData );

            if ( ( referenceProperty && this.isReferencePropertySetDeletable( referenceProperty.referencePropertySetId ) ) || id === 0 ) {
                enabled = true;
            }
        }

        if ( onClickHandler ) {
            retVal = <Button
                onClick={
                    enabled
                        ? onClickHandler
                        : this.emptyFnc
                }

                disabled={!enabled}

                icon={icon}
                className={`${className}`}
            />;
        }

        return retVal;
    }

    /**
     * Column renderer
     *
     * @private
     */
    columnBodyRenderer( rowData : RowData, columnProps : ColumnBodyOptions ) : string | JSX.Element | ( string | JSX.Element )[] | undefined {
        const {
                  schema,
                  timezone,
                  showPropertyDefaults,
                  rawMode
              } = this.props,
              {
                  referencePropertySet,
                  referenceProperty
              } = rowData,
              {
                  field
              } = columnProps;

        let retVal : string | JSX.Element | ( string | JSX.Element )[] = '',
            colValue                                                   = rowData[ field as keyof RowData ];

        switch ( field ) {
            case 'propertyTypeName':
                if ( !referencePropertySet && !colValue ) {
                    colValue = '<edit to add new one>';
                }
                break;

            case 'value':
                if ( !referenceProperty ) {
                    if ( rowData.editPropertyTypeName && !colValue ) {
                        colValue = '<edit to add new one>';
                    }
                    // get default for value
                    else if ( showPropertyDefaults && rowData.valueDefault !== undefined ) {
                        colValue = rowData.valueDefault;
                    }
                }
                break;

            case 'modificationTime':
            case 'validationTime':
            case 'validFromTime':
            case 'validToTime':
                if ( colValue ) {
                    colValue = timestampRenderer( ( colValue as string | Date | undefined ) ?? '', timezone, rawMode )
                }
                break;
        }

        if ( colValue ) {
            if ( !referenceProperty && field === 'value' && !rowData.editPropertyTypeName ) {
                retVal = statusColoringRenderer( colValue, 0, undefined, undefined, undefined, 'yn-cell-renderer-el property-default' );
            }
            else if ( !referenceProperty ) {
                retVal = <span className="yn-cell-renderer-el yn-empty">{colValue as string}</span>;
            }
            else {
                retVal = statusColoringRenderer( colValue, referenceProperty.statusId, schema, undefined, undefined, 'yn-cell-renderer-el' );
            }
        }

        return retVal;
    }

    /**
     * Builds the editor for propertyType column.
     *
     * @private
     */
    getPropertyTypeEditor( columnProps : ColumnEditorOptions & { props : { id : string } } ) : JSX.Element {
        const {
                  uid,
                  schema,
                  rawMode,
                  showAdditionalRawNames
              }       = this.props,
              {
                  insertableReferencePropertySets
              }       = this.state,
              rowData = this.state.data[ columnProps.rowIndex ],
              {
                  referencePropertySet
              }       = rowData,
              data    = insertableReferencePropertySets.map( insertableReferencePropertySet => {
                  return {
                      value: insertableReferencePropertySet.name,
                      label: getReferencePropertySetDisplayName( insertableReferencePropertySet.id, schema, rawMode, showAdditionalRawNames )
                  };
              } ),
              value   = referencePropertySet?.name ?? rowData.editPropertyTypeName ?? '';

        return <Dropdown
            key="cellEditor"
            inputId={`${uid}_propertyType`}
            value={value}
            onChange={this.onPropertyTypeEditorChange.bind( this, columnProps )}
            options={data}
            className="yn-cell-editor yn-inputgroup"
        />;
    }

    /**
     * Builds the editor for value column.
     *
     * @private
     */
    getValueEditor( columnProps : ColumnEditorOptions & { props : { id : string } } ) : string | JSX.Element | ( string | JSX.Element )[] | undefined {
        const {
                  schema,
                  timezone,
                  rawMode,
                  showAdditionalRawNames,
                  user
              }            = this.props,
              rowData      = this.state.data[ columnProps.rowIndex ],
              {
                  referencePropertySet,
                  referenceProperty,
                  error
              }            = rowData,
              table        = document.getElementById( columnProps.props.id ),
              rows         = table ? table.querySelectorAll( 'tbody > tr' ) : [],
              row          = rows[ columnProps.rowIndex ],
              height       = Math.max( row?.clientHeight ?? 0, 90 ),
              editorParams = {
                  key              : 'cellEditor',
                  id               : 'valueEditor',
                  appendToElementId: 'document',
                  autoComplete     : false,
                  autoFocus        : true,
                  classNames       : [ 'yn-cell-editor' ],
                  infoTextLayout   : 'hidden',
                  labelWidth       : 0,
                  onResize         : this.onValueEditorResize
              };

        let retVal : string | JSX.Element | ( string | JSX.Element )[] | undefined;

        if ( referencePropertySet ) {
            let value = rowData.editValue ?? referenceProperty?.value ?? referencePropertySet?.valueDefault;

            // get ValueSet editor
            if ( referencePropertySet.valueSetId ) {
                retVal = getValueSetEditor( schema, 'referenceProperty', referencePropertySet.id, value, this.onValueEditorChange.bind( this, columnProps ), rawMode, showAdditionalRawNames, editorParams );
            }

            // get ValueType editor
            else {
                retVal = [];

                if ( error ) {
                    editorParams.classNames.push( 'yn-error-border' );
                }

                if ( !value && ( referencePropertySet.valueType === 'DATE' || referencePropertySet.valueType === 'TIME' || referencePropertySet.valueType === 'TIMESTAMP' ) ) {
                    value = new Date();
                }

                const params = {
                          timezone,
                          showAdditionalRawNames,
                          valueUnit: referencePropertySet.valueUnit,
                          valuePrecision,
                          language : user?.language,
                          height
                      },
                      editor = getValueEditor( referencePropertySet.valueType, value, this.onValueEditorChange.bind( this, columnProps ), rawMode, params, editorParams );

                if ( editor ) {
                    retVal.push( editor );
                }

                if ( error ) {
                    retVal.push(
                        <Tooltip
                            key="editorTooltip"
                            target={`#${editorParams.id}`}
                            event="focus"
                            my="left top"
                            at="left bottom"
                            className="yn-tooltip"
                        >
                            <div key="error" className="yn-tooltip-error">
                                <div className="yn-tooltip-header">
                                    {__( 'YN.base.common.tooltip.error.title' )}
                                </div>
                                {parse( error )}
                            </div>
                        </Tooltip>
                    );
                }
            }
        }

        // return body renderer result to avoid flickering
        if ( !retVal ) {
            retVal = this.columnBodyRenderer( rowData, columnProps );
        }

        return retVal;
    }

    /**
     * Builds the editor for status column.
     *
     * @private
     */
    getStatusEditor( columnProps : ColumnEditorOptions ) : JSX.Element {
        const {
                  uid,
                  rawMode,
                  showAdditionalRawNames
              }       = this.props,
              rowData = this.state.data[ columnProps.rowIndex ],
              {
                  referenceProperty
              }       = rowData,
              value   = rowData.editStatus ?? referenceProperty?.status.name as Status;

        return <StatusDropdown
            uid={`${uid}_property_status`}
            key="cellEditor"
            value={value}
            onChange={this.onStatusEditorChange.bind( this, columnProps )}
            mode="settable"
            autoFocus={true}
            rawMode={rawMode}
            showAdditionalRawNames={showAdditionalRawNames}
            className="yn-cell-editor"
        />;
    }

    /**
     * REDUX stuff
     */
    render() {
        const {
                  uid,
                  status,
                  timestamp,
                  showEmptyProperties,
                  editable,
                  dataTableState,
                  user
              }               = this.props,
              {
                  insertableReferencePropertySets,
                  data
              }               = this.state,
              targetArea      = getTargetArea( status, timestamp ),
              columns         = this.getColumns( targetArea, showEmptyProperties, editable, insertableReferencePropertySets, user?.language ),
              // TODO: Should there be a message?
              //  - no property set and hidden
              //  - no PSet
              emptyMessage    = insertableReferencePropertySets.length ? __( 'YN.base.finder.noSetProperty.label' ) : __( 'YN.base.finder.noPropertySets.label' ),
              renderedColumns = document.querySelectorAll( `#${uid}_properties tr:first-child th` ) as NodeListOf<HTMLElement>;

        let tableWidth : string     = '100%',
            fullTableWidth : string = '100%';

        // avoid updating dataTable state after render
        this.ignoreDataTableSaveState = true;

        if ( Array.isArray( renderedColumns ) ) {
            const tableElement      = document.getElementById( `${uid}_properties` ),
                  tableElementWidth = tableElement?.clientWidth;

            let autoWidthColumnsCount = 0,
                fixedColumnsWidth     = 0;

            for ( const col of renderedColumns ) {
                if ( col.style?.width === 'auto' ) {
                    autoWidthColumnsCount++;
                    fixedColumnsWidth += Number( col?.style?.minWidth?.substring( 0, col?.style?.minWidth?.length - 2 ?? 0 ) ?? 0 );
                }
                else {
                    fixedColumnsWidth += Number( col?.style?.width?.substring( 0, col?.style?.width?.length - 2 ?? 0 ) ?? 0 );
                }
            }

            if ( autoWidthColumnsCount > 0 ) {
                if ( tableElementWidth && tableElementWidth < fixedColumnsWidth ) {
                    tableWidth = `${tableElementWidth}px`;
                }
                else {
                    tableWidth = '100%';
                }

                fullTableWidth = tableWidth;
            }
            else {
                tableWidth = `${fixedColumnsWidth}px`;

                if ( tableElementWidth && tableElementWidth < fixedColumnsWidth ) {
                    fullTableWidth = tableWidth;
                }
                else {
                    fullTableWidth = '100%';
                }
            }
        }

        return <div style={{ position: 'relative' }} className="yn-flex-layout-parent">
            <ContextMenu
                ref={element => this.contextMenu = element}

                model={this.getContextMenuModel( this.state.contextMenuCellInfo )}
                onHide={this.onContextMenuHide}
                style={{ width: 'auto' }}
            />
            <DataTable
                ref={element => this.dataTable = element}

                key={`${uid}_properties`}
                id={`${uid}_properties`}

                value={data}
                emptyMessage={emptyMessage}

                rowClassName={this.onResolveRowClass}
                className="p-datatable-striped yn-finder-reference-property yn-datatable-border-none-top"
                style={{ width: '100% !important', zIndex: 1 }}
                tableStyle={{ tableLayout: 'fixed', width: tableWidth }}

                resizableColumns
                columnResizeMode="expand"

                scrollable
                scrollHeight="flex"

                removableSort
                sortMode="multiple"
                multiSortMeta={dataTableState.multiSortMeta}

                lazy

                // state
                stateStorage="custom"
                customSaveState={this.onCustomSaveState}
                customRestoreState={this.onCustomRestoreState}

                contextMenuSelection={this.state.contextMenuSelection}
                onContextMenuSelectionChange={this.onContextMenuSelectionChange}
                onContextMenu={this.onContextMenu}

                pt={{
                    wrapper: {
                        style: {
                            '--table-width'   : fullTableWidth,
                            '--table-header-height': '2.2rem'
                        }
                    }
                }}
            >
                {columns}
            </DataTable>
        </div>;
    }
}

/**
 * REDUX stuff
 */
function mapStateToProps( state : StoreState ) : StateProps {
    const retVal : StateProps = {
        user: undefined
    };

    // get some session user information
    if ( state.session?.user ) {
        retVal.user = state.session.user;
    }

    return retVal;
}

/**
 * REDUX stuff
 */
function mapDispatchToProps( dispatch : Dispatch ) {
    return bindActionCreators( Actions, dispatch );
}

export default connect<StateProps, ActionProps, ComponentProps, StoreState>( mapStateToProps, mapDispatchToProps )( ReferencePropertiesDataTable );

